import MappingBase from './mappingBase.js';

/**
 * BookingService Model
 * Represents an individual service within a booking
 */
class BookingService extends MappingBase {
  /**
   * Alias mapping for field name conversion
   * Maps backend aliases to full field names
   */
  // Backend (api-salon/models/bookings.py::BookingService) uses `svcid`,
  // `seqo` and `stt`. `srvid`/`sid`, `so` and `st` are kept for back-compat
  // with any historical cached bookings or wire payloads that used those
  // earlier aliases.
  static _aliasMapping = {
    svcid: "serviceId",
    srvid: "serviceId",
    sid: "serviceId",
    sn: "serviceName",
    sdn: "serviceDisplayName",
    dur: "duration",
    pr: "price",
    seqo: "sequenceOrder",
    so: "sequenceOrder",
    stt: "startTime",
    st: "startTime",
    et: "endTime"
  };

  constructor(data = {}) {
    super();

    // Auto-convert aliases to full names
    const normalizedData = this._normalizeData(data);

    const {
      serviceId = null,
      serviceName = null,
      serviceDisplayName = null,
      duration = 0,
      price = 0,
      sequenceOrder = 0,
      startTime = null,
      endTime = null
    } = normalizedData;

    this.serviceId = serviceId;
    this.serviceName = serviceName;
    this.serviceDisplayName = serviceDisplayName;
    this.duration = duration;
    this.price = price;
    this.sequenceOrder = sequenceOrder;
    this.startTime = startTime ? new Date(startTime) : null;
    this.endTime = endTime ? new Date(endTime) : null;
  }

  /**
   * Normalize data by converting aliases to full property names
   */
  _normalizeData(data) {
    const mapping = BookingService._aliasMapping;
    const result = {};

    for (const [key, value] of Object.entries(data)) {
      const fullKey = mapping[key] || key;
      result[fullKey] = value;
    }

    return result;
  }

  /**
   * Get formatted duration string (e.g., "1h 30m")
   */
  get formattedDuration() {
    if (!this.duration) return "0m";

    const hours = Math.floor(this.duration / 60);
    const minutes = this.duration % 60;

    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  }

  /**
   * Get formatted price string
   */
  get formattedPrice() {
    return `$${this.price.toFixed(2)}`;
  }
}

export default BookingService;
